/**
 * Normalize Cognito `cognito:groups` from JWT claims.
 *
 * Supports raw arrays (Amplify / ID token payload), HTTP API JWT authorizer
 * flattening (`[Group1 Group2]`), REST Cognito authorizer (`Group1,Group2`),
 * and JSON array strings.
 */
export const parseGroupsClaim = (raw: unknown): string[] => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return stringItems(raw);
  if (typeof raw !== "string") return [];

  return parseGroupsString(raw);
};

const parseGroupsString = (raw: string): string[] => {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  if (!trimmed.startsWith("[") || !trimmed.endsWith("]")) {
    return splitGroups(trimmed, ",");
  }

  return parseBracketedGroups(trimmed);
};

const parseBracketedGroups = (raw: string): string[] => {
  const inner = raw.slice(1, -1).trim();
  if (!inner) return [];

  if (inner.includes('"')) {
    const parsed = parseJsonGroups(raw);
    if (parsed) return parsed;
  }

  return splitGroups(inner, inner.includes(",") ? "," : /\s+/);
};

const parseJsonGroups = (raw: string): string[] | null => {
  try {
    const parsed: unknown = JSON.parse(raw);

    return Array.isArray(parsed) ? stringItems(parsed) : null;
  } catch {
    return null;
  }
};

const splitGroups = (raw: string, separator: string | RegExp): string[] =>
  raw
    .split(separator)
    .map((group) => group.trim().replace(/^"(.*)"$/, "$1"))
    .filter(Boolean);

const stringItems = (values: unknown[]): string[] =>
  values.filter((value): value is string => typeof value === "string");
